import React from 'react';
import Spinner from '../../migratorDashboard/Spinner';
import { ui } from '../../../config/uiSizes';
import { getTypeLabelByApiName } from '../../../utils/resourceTypes';

interface SelectedResourcesTableBodyProps {
  filteredResourcesByType: Map<string, Resource[]>;
  filteredResourceTypes: string[];
  nameSearch: string;
  typeSearch: string;
  isFullSnapshotMode: boolean;
  resourceTypeCounts?: Map<string, number>;
  loadingResources?: boolean;
  exportedResourcesError?: Error | null;
  emptyMessage?: string;
}
type Resource = {
  id: string;
  name: string;
  type: string;
};

export const SelectedResourcesTableBody: React.FC<SelectedResourcesTableBodyProps> = ({
  filteredResourcesByType,
  filteredResourceTypes,
  typeSearch,
  nameSearch,
  isFullSnapshotMode,
  resourceTypeCounts,
  loadingResources = false,
  exportedResourcesError,
  emptyMessage = 'Please select resources from the dropdown...'
}) => {

  return (
    <>
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {loadingResources ? (
          <div className="px-5 py-8 text-center text-gray-500 dark:text-gray-400">
            <div className="flex flex-col items-center gap-2">
              <Spinner />
              <p className={`${ui.text.body}`}>Loading exported resources...</p>
            </div>
          </div>
        ) : exportedResourcesError ? (
          <div className="px-5 py-8 text-center text-gray-500 dark:text-gray-400">
            <p className={`${ui.text.body}`}>Failed to load exported resources:</p>
            <p className={`${ui.text.body} text-gray-600 dark:text-gray-400`}>
              {exportedResourcesError.message}
            </p>
          </div>
        ) : isFullSnapshotMode ? (
          // Show all resource types when in full snapshot mode
          filteredResourceTypes.length > 0 ? (
            filteredResourceTypes.map((type) => (
              <details key={type} className="group" open={!!typeSearch.trim()}>
                <summary className={`flex items-center justify-between ${ui.page.rowPadding} ${ui.gap.md} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition`}>
                  <div className="flex items-center gap-3">
                    <svg
                      className={`${ui.svg.sm} text-gray-400 transition-transform group-open:rotate-90`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100`}>
                      {getTypeLabelByApiName(type)}
                    </span>
                  </div>
                  <span className={`${ui.text.xs} font-bold text-white bg-blue-600 dark:bg-blue-700 ${ui.badge.padding} rounded-full whitespace-nowrap`}>
                    All (*)
                  </span>
                </summary>

                <div className={`${ui.page.rowPadding} bg-gray-50 dark:bg-gray-900`}>
                  <div className={`flex items-center gap-2 py-2 ${ui.text.body} text-gray-700 dark:text-gray-300`}>
                    <svg
                      className={`${ui.svg.sm} text-green-500 flex-shrink-0`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="italic">
                      All resources of this type will be included
                    </span>
                  </div>
                </div>
              </details>
            ))
          ) : (
            <div className="px-5 py-8 text-center text-gray-500 dark:text-gray-400">
              <p className={`${ui.text.body}`}>No resource types match your search</p>
            </div>
          )
        ) : // Show selected resources grouped by type
          filteredResourcesByType.size > 0 ? (
            Array.from(filteredResourcesByType.entries()).map(([type, resources]) => {
              const totalCountForType = resourceTypeCounts?.get(type);
              const isAllSelected = totalCountForType !== undefined && resources.length === totalCountForType;
              return (
                <details
                  key={type}
                  className="group"
                  open={!!nameSearch.trim()}
                >
                  <summary className={`flex items-center justify-between ${ui.page.rowPadding} ${ui.gap.md} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition`}>
                    <div className="flex items-center gap-3">
                      <svg
                        className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {getTypeLabelByApiName(type)}
                      </span>
                    </div>
                    <span className={`font-bold text-white bg-blue-600 dark:bg-blue-700 ${ui.badge.padding} rounded-full whitespace-nowrap ${ui.text.body}`}>
                      {isAllSelected ? 'All (*)' : resources.length}
                    </span>
                  </summary>

                  <div className={`${ui.page.rowPadding} bg-gray-50 dark:bg-gray-900`}>
                    {isAllSelected ? (
                      <div className={`flex items-center gap-2 py-2 ${ui.text.body} text-gray-700 dark:text-gray-300`}>
                        <svg
                          className="w-4 h-4 text-green-500 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="italic">
                          All resources of {getTypeLabelByApiName(type)} are selected
                        </span>
                      </div>

                    ) : (
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {resources.map((resource: { id: string; name: string }) => (
                          <div
                            key={resource.id}
                            className={`flex items-start gap-2 py-1 min-w-0 ${ui.text.label} text-gray-700 dark:text-gray-300`}
                          >
                            <svg
                              className={`${ui.svg.sm} text-green-500 flex-shrink-0 mt-0.5`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span className="min-w-0 break-all">{resource.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              );
            })
          ) : (
            <div className="px-5 py-8 text-center text-gray-500 dark:text-gray-400">
              {typeSearch.trim() || nameSearch.trim() ? (
                <p className={`${ui.text.body}`}>No resources match your search</p>
              ) : (
                <p className={`${ui.text.body}`}>{emptyMessage}</p>
              )}
            </div>
          )}
      </div>
    </>
  );
};

export default SelectedResourcesTableBody;
